import React, { useState } from "react"
import request from "request"
import Modal from "Modal"
import Button from "Button"
import Input from "Input"

const CreateAPIKeyModal = ({ isOpen, onClose, onSuccess }) => {
  const [name, setName] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [createdKey, setCreatedKey] = useState(null)
  const [error, setError] = useState(null)

  const handleCreate = () => {
    if (!name.trim()) {
      setError("Please enter a name for this API key")
      return
    }

    setIsLoading(true)
    setError(null)

    request.post("/venue_admin/api_keys", {
      api_key: { name: name.trim() }
    })
    .then(({ data }) => {
      setCreatedKey(data.apiKey)
      setIsLoading(false)
    })
    .catch((err) => {
      setError(err.response?.data?.error || "Failed to create API key")
      setIsLoading(false)
    })
  }

  const handleClose = () => {
    setName("")
    setCreatedKey(null)
    setError(null)
    onClose()
    if (createdKey) {
      onSuccess()
    }
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(createdKey.fullKey)
    alert("API key copied to clipboard!")
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="box">
        <h2 className="title is-4">
          {createdKey ? "API Key Created Successfully" : "Create New API Key"}
        </h2>

        {!createdKey ? (
          <>
            <div className="content">
              <p>
                Give this API key a descriptive name to help you identify it later.
                For example: "Production Integration" or "Staging Environment".
              </p>
            </div>

            <Input
              label="API Key Name"
              value={name}
              onChange={(value) => setName(value)}
              placeholder="e.g., Production Integration"
              disabled={isLoading}
            />

            {error && (
              <div className="notification is-danger is-light">
                {error}
              </div>
            )}

            <div className="buttons is-right mt-4">
              <Button
                text="Cancel"
                onClick={handleClose}
                disabled={isLoading}
              />
              <Button
                text="Create API Key"
                color="primary"
                onClick={handleCreate}
                isLoading={isLoading}
              />
            </div>
          </>
        ) : (
          <>
            <div className="notification is-warning">
              <p className="has-text-weight-bold">
                ⚠️ Important: Copy this API key now. You won't be able to see it again!
              </p>
            </div>

            <div className="field">
              <label className="label">API Key Name</label>
              <div className="control">
                <input className="input" type="text" value={createdKey.name} disabled />
              </div>
            </div>

            <div className="field">
              <label className="label">Identifier</label>
              <div className="control">
                <input
                  className="input is-family-monospace"
                  type="text"
                  value={createdKey.identifier}
                  disabled
                />
              </div>
              <p className="help">
                This identifier will be visible in logs and the API keys list.
              </p>
            </div>

            <div className="field">
              <label className="label">Full API Key</label>
              <div className="field has-addons">
                <div className="control is-expanded">
                  <input
                    className="input is-family-monospace"
                    type="text"
                    value={createdKey.fullKey}
                    readOnly
                  />
                </div>
                <div className="control">
                  <Button
                    text="Copy"
                    color="primary"
                    onClick={copyToClipboard}
                    icon="copy"
                  />
                </div>
              </div>
              <p className="help">
                Use this key in the <code>X-API-Key</code> header when making API requests.
              </p>
            </div>

            <div className="content mt-4">
              <p className="has-text-weight-bold">Next Steps:</p>
              <ol>
                <li>Copy the API key above and store it securely</li>
                <li>View the API documentation at <a href="/api-docs" target="_blank">/api-docs</a></li>
                <li>Use the key in your integration's <code>X-API-Key</code> header</li>
              </ol>
            </div>

            <div className="buttons is-right mt-4">
              <Button
                text="Done"
                color="primary"
                onClick={handleClose}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default CreateAPIKeyModal
